import { ChangeDetectionStrategy, Component, Inject } from '@angular/core'
import {
  ExternalTransaction,
  RootIdType,
  getUniqueRowIdFromExtTran,
} from '@helios/common-api'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  selectFocusedBulkExtTransactions,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { InvalidTransactionIdPopoverService } from 'libs/generic-components/src/lib/invalid-transaction-id-popover/invalid-transaction-id-popover.service'
import { TxToUpdateStatus } from './components/bulk-external-transactions-grid/bulk-external-transactions-grid.component'
import {
  BulkExternalTransactionActionType,
  BulkExternalTransactionsGetRequest,
  FindBulkExternalTransactionsForm,
} from './models/bulk-external-transactions.model'
import { BulkExternalTransactionsService } from './services/bulk-external-transactions-service/bulk-external-transactions.service'
import { createBulkExtTransGetRequest } from './utils/bulk-external-transactions-util'

@Component({
  selector: 'hs-bulk-external-transactions',
  templateUrl: './bulk-external-transactions-panel.component.html',
  styleUrls: ['./bulk-external-transactions-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkExternalTransactionsPanelComponent extends BasePanelComponent {
  isSearchPopoverShown = false
  isLoading = false
  data: ExternalTransaction[] = []
  transactionsLimitExceeded = false
  extTranIds: number[] = []
  updateStatuses$: Subject<TxToUpdateStatus> = new Subject()

  private readonly _destroyed$ = new Subject<void>()
  subscriptions: Subscription[] = []
  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly bulkService: BulkExternalTransactionsService,
    private readonly blankSessionService: BlankSessionService,
    private readonly invalidTransactionIdPopoverService: InvalidTransactionIdPopoverService,
    private readonly store: Store<RootState>,
    public toastService: ToastService,
  ) {
    super(container)
  }

  ngOnInit(): void {
    this.store
      .select(selectFocusedBulkExtTransactions)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(result => {
        if (!result || result.length === 0) return

        this.extTranIds = result

        const trans = {
          rootIdType: RootIdType.transaction,
          rootIds: result?.join(','),
        }

        this.loadBulkEditTx(trans as FindBulkExternalTransactionsForm)
      })
  }

  async onDownloadSubmit(form: FindBulkExternalTransactionsForm) {
    this.isSearchPopoverShown = false
    const request = createBulkExtTransGetRequest(form)
    this.isLoading = true
    this.updateStatuses$.next()
    //await this.getData(request)
    this.subscriptions.push(this.getData(request))
  }

  onCancelRequest() {
    this.subscriptions.forEach(s => s.unsubscribe())
    this.isLoading = false
    this.data = []
    this.isSearchPopoverShown = false
  }

  async loadBulkEditTx(form: FindBulkExternalTransactionsForm) {
    this.isLoading = true
    this.updateStatuses$.next()
    const request = createBulkExtTransGetRequest(form)
    //await this.getData(request)
    this.subscriptions.push(this.getData(request))
  }

  getData(request: BulkExternalTransactionsGetRequest) {
    const externalTranIds = request.filter.externalTranIds
    return this.bulkService.getBulkExternalTransactions(request).subscribe(
      async response => {
        this.isLoading = false
        if (!response) {
          this.data = []
          this.setMatchingTranCount('0')
          return
        }

        let missingIds: number[] = []
        if (externalTranIds) {
          const responseIds = response.data.map(resData => resData.extTranId)
          missingIds = externalTranIds.filter(
            externalTranId => !responseIds.includes(externalTranId),
          )
        }
        if (missingIds.length > 0) {
          try {
            await this.invalidTransactionIdPopoverService.toggle(missingIds)
          } catch (error) {
            return
          }
        }

        this.isSearchPopoverShown = false
        this.data = response.data
        this.setMatchingTranCount(response.matchingTransCount)
      },
      error => {
        this.toastService.error(
          `Error getting bulk external transactions: ${error.error.errorId}`,
        )
        this.isLoading = false
      },
    )
  }

  clearTable() {
    this.data = []
  }

  setMatchingTranCount(matchingTranCount: string) {
    this.transactionsLimitExceeded = matchingTranCount?.endsWith('+')
  }

  onIgnore(transactions: ExternalTransaction[]) {
    this.updateBulkAction(transactions, 'IGNORE')
  }

  onUnignore(transactions: ExternalTransaction[]) {
    this.updateBulkAction(transactions, 'UNIGNORE')
  }

  onHold(transactions: ExternalTransaction[]) {
    this.updateBulkAction(transactions, 'HOLD')
  }

  onUnhold(transactions: ExternalTransaction[]) {
    this.updateBulkAction(transactions, 'UNHOLD')
  }

  async updateBulkAction(
    transactions: ExternalTransaction[],
    action: BulkExternalTransactionActionType,
  ) {
    const uniqueTranIds = transactions.map(t => getUniqueRowIdFromExtTran(t))
    this.updateStatuses$.next({
      uniqueTranIds: uniqueTranIds,
      requestStatus: 'LOADING',
    })
    await this.blankSessionService.createBlankSessionIfNotInSession()

    try {
      await this.bulkService.updateTransactions(
        transactions.map(x => x.extTranId),
        action,
      )

      this.updateStatuses$.next({
        uniqueTranIds: uniqueTranIds,
        requestStatus: 'SUCCESS',
      })
    } catch {
      this.updateStatuses$.next({
        uniqueTranIds: uniqueTranIds,
        requestStatus: 'FAILURE',
      })
    }
  }
}
